import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  computed,
  effect,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import { CapabilitiesService } from '@sisoma/auth';
import { Icon } from '@sisoma/ui';

import { PartnersData, type Partner } from '../../data/partners.data';

/**
 * The partner switcher — a shell control (mounted in the sidenav footer) that
 * shows the active partner and opens a searchable "Select a partner" modal,
 * modeled on the legacy Select-a-Creator picker. Choosing a partner sets it as
 * active (which the nav, capability gating, and the `creatorSub` header all
 * react to) and lands on the dashboard.
 *
 * Native `<dialog>` + design-token markup, matching the invites/billing
 * features — there are no `@sisoma/ui` dialog/button/input primitives yet.
 */
@Component({
  selector: 'app-partner-switcher',
  imports: [Icon],
  templateUrl: './partner-switcher.html',
  styleUrl: './partner-switcher.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PartnerSwitcher {
  private readonly capabilities = inject(CapabilitiesService);
  private readonly router = inject(Router);
  protected readonly data = inject(PartnersData);

  /** Drives the native dialog open/closed. */
  protected readonly open = signal(false);
  /** The current search text. */
  protected readonly query = signal('');

  protected readonly activeId = this.capabilities.activePartnerId;

  private readonly partners = computed<Partner[]>(() => this.data.partners.value() ?? []);

  /** Label for the trigger: the active partner's name, or a prompt. */
  protected readonly activeName = computed(() => {
    const id = this.activeId();
    if (!id) {
      return 'Select partner';
    }
    return this.partners()?.find((partner) => partner.id === id)?.name ?? 'Partner';
  });

  /** Partners matching the search box (case-insensitive on name / owner email). */
  protected readonly filtered = computed<Partner[]>(() => {
    const query = this.query()?.trim()?.toLowerCase();
    const all = this.partners();
    if (!query) {
      return all;
    }
    return all.filter(
      (partner) =>
        partner.name.toLowerCase().includes(query) ||
        (partner.ownerEmail?.toLowerCase().includes(query) ?? false),
    );
  });

  private readonly dialog = viewChild<ElementRef<HTMLDialogElement>>('pickerDialog');

  constructor() {
    // Keep the native <dialog> in step with `open`, so it shows with a backdrop
    // (showModal) and closes on select / cancel / Esc.
    effect(() => {
      const dialog = this.dialog()?.nativeElement;
      if (!dialog) {
        return;
      }
      if (this.open() && !dialog.open) {
        dialog.showModal();
      } else if (!this.open() && dialog.open) {
        dialog.close();
      }
    });
  }

  protected openPicker(): void {
    this.query.set('');
    this.open.set(true);
  }

  protected closePicker(): void {
    this.open.set(false);
  }

  protected select(partner: Partner): void {
    // Sets the active partner (persisted in @sisoma/auth) and re-derives caps,
    // nav and the creatorSub header from it.
    this.capabilities.setActivePartnerId(partner.id);
    this.open.set(false);
    void this.router.navigate(['dashboard']);
  }
}
